// Персистентные «Мои партии»: Telegram CloudStorage, localStorage как браузерный
// фолбэк. Индекс отделён от PGN, чтобы список не читал до 50 тяжёлых значений.
import { Chess } from 'chess.js';
import type { CoachGame } from '@ext/core/game-sources';
import type { Recorded, PlayResult } from './play-engine';
import { cloudStorage } from '../tg';

const INDEX_KEY = 'cc_my_index';
const GAME_PREFIX = 'cc_my_g_';
export const MY_GAMES_MAX = 50;

export interface MyGameMeta {
  id: string;
  date: string;
  side: 'w' | 'b' | null;
  result: string;
  opponent: string;
  source: 'played' | 'saved';
  sourceRef?: string;
  fen?: string;
  sharedId?: string;
  title?: string;
}

function localGet(key: string): string | null { try { return localStorage.getItem(key); } catch { return null; } }
function localSet(key: string, value: string): void { try { localStorage.setItem(key, value); } catch { throw new Error('storage'); } }
function localRemove(key: string): void { try { localStorage.removeItem(key); } catch { /* noop */ } }

async function get(key: string): Promise<string | null> {
  const cloud = cloudStorage();
  if (!cloud) return localGet(key);
  return new Promise((resolve) => cloud.getItem(key, (e, value) => resolve(e ? localGet(key) : value || null)));
}
async function set(key: string, value: string): Promise<void> {
  const cloud = cloudStorage();
  if (!cloud) return localSet(key, value);
  await new Promise<void>((resolve, reject) => cloud.setItem(key, value, (e) => e ? reject(new Error(e)) : resolve()));
  localSet(key, value);
}
async function remove(key: string): Promise<void> {
  const cloud = cloudStorage();
  if (cloud) await new Promise<void>((resolve) => cloud.removeItem(key, () => resolve()));
  localRemove(key);
}

export async function listMyGames(): Promise<MyGameMeta[]> {
  try {
    const parsed = JSON.parse((await get(INDEX_KEY)) || '[]');
    return Array.isArray(parsed) ? parsed.filter((x): x is MyGameMeta => Boolean(x?.id && x?.date)).slice(0, MY_GAMES_MAX) : [];
  } catch { return []; }
}

export async function readMyGame(id: string): Promise<string | null> { return get(GAME_PREFIX + id); }

export async function saveMyGame(meta: MyGameMeta, pgn: string): Promise<void> {
  const index = await listMyGames();
  const existing = index.findIndex((x) => x.id === meta.id || (meta.sourceRef && x.sourceRef === meta.sourceRef));
  if (existing < 0 && index.length >= MY_GAMES_MAX) throw new Error('limit');
  if (existing >= 0) index.splice(existing, 1);
  index.unshift(meta);
  await set(GAME_PREFIX + meta.id, pgn);
  try { await set(INDEX_KEY, JSON.stringify(index)); }
  catch (e) { await remove(GAME_PREFIX + meta.id); throw e; }
}

export async function deleteMyGame(id: string): Promise<void> {
  await remove(GAME_PREFIX + id);
  await set(INDEX_KEY, JSON.stringify((await listMyGames()).filter((x) => x.id !== id)));
}

export async function renameMyGame(id: string, title: string): Promise<void> {
  const index = await listMyGames();
  const game = index.find((x) => x.id === id);
  if (!game) return;
  game.title = title.trim().slice(0, 64) || undefined;
  await set(INDEX_KEY, JSON.stringify(index));
}

export function metaFromCoachGame(game: CoachGame): MyGameMeta {
  return { id: `saved_${game.site}_${game.id}`.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 90), date: new Date().toISOString(), side: null,
    result: game.result || '*', opponent: [game.white, game.black].filter(Boolean).join(' — '), source: 'saved', sourceRef: `${game.site}:${game.id}` };
}

export function pgnFromRecord(record: Recorded[], side: 'w' | 'b', result: PlayResult, startFen?: string): string {
  const chess = new Chess();
  if (startFen) chess.load(startFen, { skipValidation: true });
  chess.setHeader('Event', 'ChessCoach My Game');
  chess.setHeader('Date', new Date().toISOString().slice(0, 10).replace(/-/g, '.'));
  chess.setHeader('White', side === 'w' ? 'Me' : 'Stockfish');
  chess.setHeader('Black', side === 'b' ? 'Me' : 'Stockfish');
  for (const move of record) chess.move(move.san, { strict: false });
  let score = '*';
  if (result === 'checkmate') score = chess.turn() === 'w' ? '0-1' : '1-0';
  else if (result) score = '1/2-1/2';
  chess.setHeader('Result', score);
  return chess.pgn();
}

const API = 'https://api.getchesscoach.com/v1/mini/shared-games';
export async function publishMyGame(pgn: string, name?: string): Promise<string> {
  const res = await fetch(API, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ pgn, name }), credentials: 'omit' });
  const data = await res.json() as { ok?: boolean; id?: string };
  if (!res.ok || !data.ok || !data.id) throw new Error('share');
  return data.id;
}
export async function fetchSharedMyGame(id: string): Promise<string> {
  const res = await fetch(`${API}/${encodeURIComponent(id)}`, { credentials: 'omit' });
  const data = await res.json() as { ok?: boolean; pgn?: string };
  if (!res.ok || !data.ok || !data.pgn) throw new Error('not_found');
  return data.pgn;
}
